import { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { scaleLinear } from "d3-scale";

// World atlas (110m) from CDN — small and fast (~120 KB).
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Mapping from ISO-2 (returned by ipapi / ip-api) → ISO-3 country code which is what
// world-atlas/topojson uses internally as `properties.name`. We rely on name match below.
// Direct name match is simpler — we keep an ISO-2 → English country name dict for the
// most common cases (covers >99% of typical hits, fallback uses the country_name field).
const ISO2_TO_NAME = {
  US: "United States of America", FR: "France", DE: "Germany", GB: "United Kingdom",
  IT: "Italy", ES: "Spain", BE: "Belgium", NL: "Netherlands", CH: "Switzerland",
  CA: "Canada", MX: "Mexico", BR: "Brazil", AR: "Argentina", CN: "China", JP: "Japan",
  KR: "South Korea", IN: "India", RU: "Russia", AU: "Australia", NZ: "New Zealand",
  ZA: "South Africa", EG: "Egypt", MA: "Morocco", DZ: "Algeria", TN: "Tunisia",
  SN: "Senegal", ML: "Mali", MR: "Mauritania", CI: "Ivory Coast", BF: "Burkina Faso",
  NE: "Niger", GN: "Guinea", TG: "Togo", BJ: "Benin", GH: "Ghana", NG: "Nigeria",
  CM: "Cameroon", CD: "Dem. Rep. Congo", CG: "Republic of the Congo", GA: "Gabon",
  ET: "Ethiopia", KE: "Kenya", TZ: "Tanzania", UG: "Uganda", RW: "Rwanda", BI: "Burundi",
  AO: "Angola", MZ: "Mozambique", ZM: "Zambia", ZW: "Zimbabwe", NA: "Namibia",
  BW: "Botswana", MG: "Madagascar", SA: "Saudi Arabia", AE: "United Arab Emirates",
  TR: "Turkey", IR: "Iran", IL: "Israel", PK: "Pakistan", BD: "Bangladesh",
  TH: "Thailand", VN: "Vietnam", ID: "Indonesia", PH: "Philippines", MY: "Malaysia",
  SG: "Singapore", SE: "Sweden", NO: "Norway", FI: "Finland", DK: "Denmark", IE: "Ireland",
  PT: "Portugal", PL: "Poland", AT: "Austria", CZ: "Czechia", RO: "Romania", GR: "Greece",
  HU: "Hungary", BG: "Bulgaria", UA: "Ukraine",
};

export default function AnalyticsMap({ countries = [], focus = "africa" }) {
  const [hovered, setHovered] = useState(null);

  const countryMap = useMemo(() => {
    const m = new Map();
    for (const c of countries) {
      const name = ISO2_TO_NAME[c.country] || c.country_name;
      if (name) m.set(name.toLowerCase(), c.n);
    }
    return m;
  }, [countries]);

  const maxN = Math.max(1, ...countries.map((c) => c.n));
  const colorScale = useMemo(
    () => scaleLinear().domain([0, maxN]).range(["#FDFBF7", "#D92D20"]),
    [maxN]
  );

  // Center/zoom config — Africa-focused (since the platform is francophone Africa first)
  const projectionConfig = focus === "africa"
    ? { scale: 360, center: [18, 4] } // West/Central Africa
    : { scale: 150, center: [0, 20] };

  const total = countries.reduce((s, c) => s + c.n, 0);

  return (
    <div className="border border-black bg-[var(--paper)] relative" data-testid="analytics-map">
      <div className="flex items-center justify-between px-4 py-3 border-b border-black">
        <div>
          <div className="label-mono text-[var(--muted)]">§ Géographie de l'audience</div>
          <div className="font-serif text-lg">
            {total.toLocaleString()} pages vues réparties sur {countries.length} pays
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="label-mono text-[var(--muted)]">Échelle</span>
          <div className="w-32 h-3 border border-black" style={{ background: "linear-gradient(90deg,#FDFBF7,#D92D20)" }} />
          <span className="font-mono">{maxN}</span>
        </div>
      </div>
      <div style={{ width: "100%", aspectRatio: "1.6 / 1", background: "#F5F0E5" }}>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={projectionConfig}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup minZoom={0.8} maxZoom={6}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = (geo.properties.name || "").toLowerCase();
                  const value = countryMap.get(name) || 0;
                  const fill = value > 0 ? colorScale(value) : "#FDFBF7";
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fill}
                      stroke="#0F1115"
                      strokeWidth={0.3}
                      onMouseEnter={() => setHovered({ name: geo.properties.name, value })}
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        default: { outline: "none" },
                        hover: { outline: "none", fill: value > 0 ? "#0F1115" : "#E5DFD3", cursor: "pointer" },
                        pressed: { outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>
      {hovered && (
        <div className="absolute top-16 left-4 bg-[var(--ink)] text-[var(--paper)] p-3 pointer-events-none font-mono text-xs" data-testid="analytics-map-tooltip">
          <div className="label-mono text-[var(--paper)]/70">{hovered.name}</div>
          <div className="text-lg font-serif">{hovered.value}</div>
          <div className="label-mono text-[var(--paper)]/70">pages vues</div>
        </div>
      )}
    </div>
  );
}
